import { h, defineComponent, ref, computed, watch } from 'vue'
import { slotContent } from './primitives'
const get = (item, key) => key.split('.').reduce((value, part) => value?.[part], item)
export const BTable = defineComponent({
  inheritAttrs: false,
  props: {
    items: { default: () => [] },
    fields: Array,
    small: Boolean,
    striped: Boolean,
    hover: Boolean,
    bordered: Boolean,
    borderless: Boolean,
    responsive: [Boolean, String],
    stacked: [Boolean, String],
    busy: Boolean,
    showEmpty: Boolean,
    emptyText: { default: 'There are no records to show' },
    emptyFilteredText: { default: 'There are no records matching your request' },
    sortBy: String,
    sortDesc: Boolean,
    sortCompare: Function,
    noLocalSorting: Boolean,
    noSortReset: Boolean,
    perPage: { default: 0 },
    currentPage: { default: 1 },
    filter: [String, RegExp],
    tbodyTrClass: [String, Function],
    primaryKey: String
  },
  emits: ['sort-changed', 'update:sortBy', 'update:sortDesc', 'row-clicked', 'filtered'],
  setup(p, { slots, emit, attrs }) {
    const key = ref(p.sortBy)
    const desc = ref(p.sortDesc)
    watch(
      () => p.sortBy,
      (v) => (key.value = v)
    )
    watch(
      () => p.sortDesc,
      (v) => (desc.value = v)
    )
    const fields = computed(() => {
      const seen = new Set()
      return (p.fields || Object.keys(p.items[0] || {}).filter((k) => !k.startsWith('_'))).map((f) =>
        typeof f === 'string'
          ? { key: f, label: f.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase()) }
          : {
              ...f,
              label: f.label ?? f.key.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase())
            }
      ).filter((field) => {
        // BootstrapVue keeps the first definition for each field key, including
        // its label/formatter. Normalize once for headers, cells and colspans.
        if (seen.has(field.key)) return false
        seen.add(field.key)
        return true
      })
    })
    const filtered = computed(() =>
      p.filter
        ? p.items.filter((item) => {
            const flatten = (value) =>
              value && typeof value === 'object'
                ? Object.keys(value)
                    .filter(
                      (key) => !['_rowVariant', '_cellVariants', '_showDetails'].includes(key)
                    )
                    .sort()
                    .map((key) => flatten(value[key]))
                    .join(' ')
                : String(value ?? '')
            const value = flatten(item)
            if (p.filter instanceof RegExp) p.filter.lastIndex = 0
            return p.filter instanceof RegExp
              ? p.filter.test(value)
              : value.toLowerCase().includes(p.filter.toLowerCase())
          })
        : p.items
    )
    watch(filtered, (rows) => emit('filtered', rows, rows.length))
    const rows = computed(() => {
      let items = [...filtered.value]
      if (key.value && !p.noLocalSorting)
        items.sort((a, b) => {
          let order = p.sortCompare?.(
            a,
            b,
            key.value,
            desc.value,
            fields.value.find((field) => field.key === key.value)?.formatter,
            { numeric: true },
            undefined
          )
          if (order === undefined || order === null || order === false) {
            const av = get(a, key.value),
              bv = get(b, key.value)
            order =
              typeof av === 'number' && typeof bv === 'number'
                ? av - bv
                : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true })
          }
          return desc.value ? -order : order
        })
      return Number(p.perPage) > 0
        ? items.slice((p.currentPage - 1) * p.perPage, p.currentPage * p.perPage)
        : items
    })
    const sort = (field) => {
      if (!fields.value.some((f) => f.sortable)) return
      if (field.sortable) {
        desc.value = key.value === field.key ? !desc.value : field.sortDirection === 'desc'
        key.value = p.noLocalSorting && field.sortKey ? field.sortKey : field.key
      } else {
        if (!key.value || p.noSortReset) return
        key.value = ''
        desc.value = false
      }
      emit('update:sortBy', key.value)
      emit('update:sortDesc', desc.value)
      emit('sort-changed', { sortBy: key.value, sortDesc: desc.value })
    }
    return () => {
      const table = h(
        'table',
        {
          ...attrs,
          class: [
            attrs.class,
            'table b-table',
            p.small && 'table-sm',
            p.striped && 'table-striped',
            p.hover && 'table-hover',
            p.bordered && 'table-bordered',
            p.borderless && 'table-borderless',
            p.stacked && (p.stacked === true ? 'b-table-stacked' : `b-table-stacked-${p.stacked}`)
          ],
          'aria-busy': p.busy
        },
        [
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              fields.value.map((field) =>
                h(
                  'th',
                  {
                    scope: 'col',
                    tabindex: field.sortable ? 0 : undefined,
                    role: 'columnheader',
                    onClick: (e) => {
                      if (!e.target.closest('input,select,textarea,button,a')) sort(field)
                    },
                    onKeydown: (e) => {
                      if (field.sortable && ['Enter', ' '].includes(e.key)) {
                        e.preventDefault()
                        sort(field)
                      }
                    },
                    class: [field.class, field.thClass],
                    style: field.thStyle,
                    'aria-sort': field.sortable
                      ? key.value === field.key
                        ? desc.value
                          ? 'descending'
                          : 'ascending'
                        : 'none'
                      : undefined
                  },
                  // Forwarded optional Yega headers can contain only Vue 3
                  // comment/fragment nodes. Preserve BootstrapVue's label fallback.
                  slotContent(
                    () => slots[`head(${field.key})`]?.({
                      label: field.label, field, column: field.key
                    }),
                    field.label
                  )
                )
              )
            )
          ),
          h(
            'tbody',
            {},
            p.busy
              ? [
                  h(
                    'tr',
                    {},
                    h('td', { colspan: fields.value.length }, slots['table-busy']?.() || 'Loading…')
                  )
                ]
              : rows.value.length
              ? rows.value.flatMap((item, index) => {
                  const data = {
                    item,
                    index,
                    detailsShowing: !!item._showDetails,
                    toggleDetails: () => (item._showDetails = !item._showDetails)
                  }
                  return [
                    h(
                      'tr',
                      {
                        key: p.primaryKey ? item[p.primaryKey] : item.id ?? index,
                        class:
                          typeof p.tbodyTrClass === 'function'
                            ? p.tbodyTrClass(item, 'row')
                            : p.tbodyTrClass,
                        onClick: (event) => emit('row-clicked', item, index, event)
                      },
                      fields.value.map((field) => {
                        const raw = get(item, field.key)
                        const value =
                          typeof field.formatter === 'function'
                            ? field.formatter(raw, field.key, item)
                            : raw
                        const content =
                          slots[`cell(${field.key})`]?.({
                            ...data,
                            field,
                            value,
                            unformatted: raw
                          }) ||
                          slots['cell()']?.({ ...data, field, value }) ||
                          String(value ?? '')
                        return h(
                          'td',
                          {
                            class: [
                              field.class,
                              typeof field.tdClass === 'function'
                                ? field.tdClass(value, field.key, item)
                                : field.tdClass
                            ],
                            'data-label': field.label
                          },
                          p.stacked ? [h('div', content)] : content
                        )
                      })
                    ),
                    item._showDetails &&
                      slots['row-details'] &&
                      h(
                        'tr',
                        { key: `details-${item.id ?? index}` },
                        h('td', { colspan: fields.value.length }, slots['row-details'](data))
                      )
                  ]
                })
              : p.showEmpty
              ? [
                  h(
                    'tr',
                    {},
                    h(
                      'td',
                      { colspan: fields.value.length },
                      h(
                        'div',
                        { role: 'alert', 'aria-live': 'polite' },
                        slots.empty?.() ||
                          h(
                            'div',
                            { class: 'text-center my-2' },
                            p.filter ? p.emptyFilteredText : p.emptyText
                          )
                      )
                    )
                  )
                ]
              : []
          )
        ]
      )
      return p.responsive
        ? h(
            'div',
            {
              class: p.responsive === true ? 'table-responsive' : `table-responsive-${p.responsive}`
            },
            [table]
          )
        : table
    }
  }
})
export const BTableLite = defineComponent({
  inheritAttrs: false,
  props: BTable.props,
  setup:
    (p, { attrs, slots }) =>
    () =>
      h(
        BTable,
        {
          ...attrs,
          ...p,
          sortBy: undefined,
          noLocalSorting: true,
          fields: p.fields?.map((field) =>
            typeof field === 'string' ? field : { ...field, sortable: false }
          )
        },
        slots
      )
})
// Default pagination algorithm and responsive presentation follow BootstrapVue 2.21.2 (MIT).
export const BPagination = defineComponent({
  props: {
    modelValue: Number,
    value: Number,
    totalRows: { default: 0 },
    perPage: { default: 20 },
    disabled: Boolean,
    size: String,
    limit: { default: 5 },
    align: String,
    pills: Boolean
  },
  emits: ['update:modelValue', 'input', 'change', 'page-click'],
  setup(p, { emit }) {
    const page = () => p.modelValue ?? p.value ?? 1
    const update = (value) => {
      emit('update:modelValue', value)
      emit('input', value)
    }
    watch(
      () => [p.perPage, p.totalRows],
      ([per, total], [oldPer, oldTotal]) => {
        if ((per !== oldPer && total === oldTotal) || page() > Math.max(1, Math.ceil(total / per)))
          update(1)
      }
    )
    const select = (value, e) => {
      if (p.disabled || page() === value) return
      const event = {
        type: 'page-click',
        target: e.target,
        cancelable: true,
        defaultPrevented: false,
        preventDefault() {
          this.defaultPrevented = true
        }
      }
      emit('page-click', event, value)
      if (event.defaultPrevented) return
      update(value)
      emit('change', value)
    }
    return () => {
      const total = Math.max(1, Math.ceil(Number(p.totalRows) / Math.max(1, Number(p.perPage))))
      const current = Math.max(1, Math.min(page(), total)),
        limit = Math.max(1, Number(p.limit) || 5)
      let count = limit,
        start = 1,
        firstDots = false,
        lastDots = false
      if (total <= limit) count = total
      else if (current < limit - 1 && limit > 3) {
        lastDots = true
        count = limit - 1
      } else if (total - current + 2 < limit && limit > 3) {
        firstDots = true
        count = limit - 1
        start = total - count + 1
      } else {
        if (limit > 3) {
          count = limit - 2
          firstDots = true
          lastDots = true
        }
        start = current - Math.floor(count / 2)
      }
      if (start < 1) {
        start = 1
        firstDots = false
      } else if (start > total - count) {
        start = total - count + 1
        lastDots = false
      }
      count = Math.min(count, total - start + 1)
      const mobileStart = Math.max(start, Math.min(current - 1, start + count - 3))
      const button = (label, target, disabled = false, isPage = false, hidden = false) =>
        h(
          'li',
          {
            role: 'presentation',
            class: [
              'page-item',
              disabled && 'disabled',
              isPage && target === current && 'active',
              hidden && 'bv-d-xs-down-none'
            ]
          },
          [
            h(
              disabled ? 'span' : 'button',
              {
                type: disabled ? undefined : 'button',
                role: disabled ? undefined : 'menuitemradio',
                class: 'page-link',
                'aria-label': isPage
                  ? `Go to page ${target}`
                  : label === '«'
                  ? 'Go to first page'
                  : label === '‹'
                  ? 'Go to previous page'
                  : label === '›'
                  ? 'Go to next page'
                  : 'Go to last page',
                'aria-checked': disabled ? undefined : String(isPage && target === current),
                'aria-posinset': isPage ? target : undefined,
                'aria-setsize': isPage ? total : undefined,
                tabindex: disabled ? undefined : isPage && target === current ? 0 : -1,
                onClick: (e) => select(target, e)
              },
              label
            )
          ]
        )
      const dots = () =>
        h(
          'li',
          { role: 'separator', class: 'page-item disabled bv-d-xs-down-none' },
          h('span', { class: 'page-link' }, '…')
        )
      return h(
        'ul',
        {
          role: 'menubar',
          'aria-label': 'Pagination',
          class: [
            'pagination',
            'b-pagination',
            p.size && `pagination-${p.size}`,
            p.pills && 'b-pagination-pills',
            p.align && `justify-content-${p.align === 'fill' ? 'between' : p.align}`
          ],
          onKeydown: (e) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
            const items = [...e.currentTarget.querySelectorAll('button')].filter(
              (el) => el.getClientRects().length
            )
            const index = items.indexOf(e.target)
            e.preventDefault()
            items[
              e.key === 'Home'
                ? 0
                : e.key === 'End'
                ? items.length - 1
                : Math.max(0, Math.min(items.length - 1, index + (e.key === 'ArrowRight' ? 1 : -1)))
            ]?.focus()
          }
        },
        [
          button('«', 1, p.disabled || current === 1),
          button('‹', current - 1, p.disabled || current === 1),
          firstDots && dots(),
          ...Array.from({ length: count }, (_, i) =>
            button(
              String(start + i),
              start + i,
              p.disabled,
              true,
              count > 3 && (start + i < mobileStart || start + i > mobileStart + 2)
            )
          ),
          lastDots && dots(),
          button('›', current + 1, p.disabled || current === total),
          button('»', total, p.disabled || current === total)
        ]
      )
    }
  }
})
